import { useEffect, useState, useCallback } from 'react'
import { FileText, Download, CheckCircle2, Clock, ShieldCheck } from 'lucide-react'
import api from '../api/client'
import Loader from '../components/Loader'
import { successModal, errorModal, confirmModal } from '../components/ui'

// Meus Documentos (licenciado): termos/documentos disponíveis, pendências de aceite e histórico.
const fmt = (v) => (v ? new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '-')
// PDFs ficam no host da API (/media); o build de produção não tem VITE_API_URL, então deriva do client.
const API = (import.meta.env.VITE_API_URL || api.defaults.baseURL || '').replace(/\/api\/?$/, '')
const fileUrl = (u) => (!u ? null : (/^https?:/.test(u) ? u : `${API}${u}`))

export default function MeusDocumentos() {
  const [docs, setDocs] = useState([])
  const [pendentes, setPendentes] = useState([])
  const [aceites, setAceites] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(0)

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([
      api.get('/termos/documentos').then((r) => r.data?.data || []).catch(() => []),
      api.get('/termos/pendentes').then((r) => r.data?.pendentes || []).catch(() => []),
      api.get('/termos/meus-aceites').then((r) => r.data?.aceites || []).catch(() => []),
    ]).then(([d, p, a]) => { setDocs(d); setPendentes(p); setAceites(a) }).finally(() => setLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  async function aceitar(t) {
    if (!(await confirmModal(`Confirmar o aceite de "${t.nome}" (versão ${t.versao})?`, { title: 'Aceite de documento', okLabel: 'Aceitar' }))) return
    setBusy(t.id)
    try {
      await api.post(`/termos/${t.id}/aceitar`)
      await successModal('Documento aceito com sucesso.', { title: 'Aceite registrado' })
      load()
    } catch (e) { errorModal(e?.response?.data?.detail || 'Não foi possível registrar o aceite') } finally { setBusy(0) }
  }

  if (loading) return <div className="flex justify-center py-20"><Loader text="Carregando documentos..." /></div>

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-xl sm:text-2xl font-heading font-extrabold text-graphite flex items-center gap-2">
          <ShieldCheck size={24} className="text-accent-500" /> Meus Documentos
        </h1>
        <p className="text-sm text-gray-500 mt-1">Termos e documentos da sua licença — pendências, aceites e histórico.</p>
      </div>

      {pendentes.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <p className="font-heading font-bold text-amber-800 flex items-center gap-2 mb-3"><Clock size={18} /> Pendentes de aceite ({pendentes.length})</p>
          <div className="space-y-2">
            {pendentes.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 bg-white rounded-xl border border-amber-100 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-graphite">{t.nome} <span className="text-xs text-gray-400">v{t.versao}</span></p>
                  {t.descricao && <p className="text-xs text-gray-500 truncate max-w-md">{t.descricao}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {fileUrl(t.arquivo_url) && <a href={fileUrl(t.arquivo_url)} target="_blank" rel="noreferrer" className="text-xs text-accent-700 border border-accent-200 rounded-lg px-2.5 py-1.5 hover:bg-accent-50 inline-flex items-center gap-1"><Download size={13} /> Ler</a>}
                  <button onClick={() => aceitar(t)} disabled={busy === t.id} className="text-xs font-bold text-white bg-accent-500 hover:bg-accent-600 rounded-lg px-3 py-1.5 disabled:opacity-50">Aceitar</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <p className="font-heading font-bold text-graphite flex items-center gap-2 mb-3"><FileText size={18} className="text-accent-500" /> Documentos disponíveis</p>
        {docs.length === 0 ? <p className="text-sm text-gray-400 py-4 text-center">Nenhum documento publicado.</p> : (
          <div className="divide-y divide-gray-50">
            {docs.map((t) => {
              const aceito = aceites.find((a) => (a.termo_id || a.slug) === (t.id || t.slug))
              return (
                <div key={t.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-graphite">{t.nome} <span className="text-xs text-gray-400">v{t.versao}</span>{t.obrigatorio && <span className="ml-2 text-[10px] uppercase font-bold text-rose-600">obrigatório</span>}</p>
                    {t.descricao && <p className="text-xs text-gray-500 truncate max-w-lg">{t.descricao}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {aceito && <span className="text-[11px] font-bold text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={13} /> aceito</span>}
                    {fileUrl(t.arquivo_url) && <a href={fileUrl(t.arquivo_url)} target="_blank" rel="noreferrer" className="text-xs text-accent-700 border border-accent-200 rounded-lg px-2.5 py-1.5 hover:bg-accent-50 inline-flex items-center gap-1"><Download size={13} /> Abrir</a>}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <p className="font-heading font-bold text-graphite flex items-center gap-2 mb-3"><CheckCircle2 size={18} className="text-emerald-600" /> Histórico de aceites</p>
        {aceites.length === 0 ? <p className="text-sm text-gray-400 py-4 text-center">Você ainda não aceitou nenhum documento.</p> : (
          <table className="w-full text-sm">
            <thead className="text-gray-400 text-xs uppercase text-left"><tr><th className="py-1.5">Documento</th><th className="py-1.5">Versão</th><th className="py-1.5 text-right">Data</th></tr></thead>
            <tbody>
              {aceites.map((a, i) => (
                <tr key={i} className="border-t border-gray-50">
                  <td className="py-2 text-gray-700">{a.nome || a.termo_nome || a.slug}</td>
                  <td className="py-2 text-gray-500">v{a.versao ?? '-'}</td>
                  <td className="py-2 text-right text-gray-500 text-xs">{fmt(a.data || a.aceito_em || a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
